import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HandThumbUpIcon, HandThumbDownIcon, ChatBubbleLeftIcon, PencilIcon, TrashIcon, LockClosedIcon, LockOpenIcon } from '@heroicons/react/20/solid';
import { formatDate } from '../../utils/helpers';
import { activityApi } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { usePageviewTracker } from '../../hooks/usePageviewTracker';
import { LoadingButton } from '../common/LoadingComponents';
import { ROUTES } from '../../utils/constants';
import { getActivityRole } from '../../utils/accessibility';
import { sanitizeHtml } from '../../utils/htmlSanitizer';
import CommentSection from './CommentSection';

function ActivityCard({ 
  activity, 
  currentUser, 
  isAuthenticated, 
  onEdit, 
  onDelete,
  onUpdate, 
  onReaction 
}) {
  const [showComments, setShowComments] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [locking, setLocking] = useState(false);
  const toast = useToast();

  // Track pageview when activity comments are expanded
  usePageviewTracker({
    contentId: activity._id,
    contentType: 'activity',
    contentTypeCategory: activity.type
  });

  const canEdit = isAuthenticated && (
    currentUser?.id === activity.authorId || 
    currentUser?.roles?.includes('admin')
  );

  const isAdmin = isAuthenticated && currentUser?.roles?.includes('admin');
  const isLocked = activity.locked || false;

  const handleDelete = async () => {
    if (!canEdit || deleting) return;
    
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    setDeleting(true);
    
    try {
      await activityApi.deleteActivity(activity._id);
      onDelete(activity._id);
      toast.success('Post deleted successfully!');
    } catch (err) {
      console.error('Failed to delete activity:', err);
      toast.error('Unable to delete post. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleLock = async () => {
    if (!isAdmin || locking) return;
    
    const action = isLocked ? 'unlock' : 'lock';
    if (!window.confirm(`Are you sure you want to ${action} this thread?`)) {
      return;
    }

    setLocking(true);
    
    try {
      const updated = await activityApi.lockActivity(activity._id, !isLocked);
      onUpdate(updated);
      toast.success(`Thread ${action}ed successfully!`);
    } catch (err) {
      console.error(`Failed to ${action} thread:`, err);
      toast.error(`Unable to ${action} thread. Please try again.`);
    } finally {
      setLocking(false);
    }
  };

  const getTypeStyles = (type) => {
    switch (type) {
      case 'event': 
        return { bg: '#e7f5ec', border: '#28a745', text: '#1e7e34' };
      case 'discussion': 
        return { bg: '#e3f2fd', border: '#17a2b8', text: '#0c5460' };
      case 'announcement':
      default: 
        return { bg: '#f3e8ff', border: '#6f42c1', text: '#5a32a3' };
    }
  };

  const userVote = activity.reactions?.userVotes?.[currentUser?.id];
  const typeStyles = getTypeStyles(activity.type);
  const role = getActivityRole(activity.type);

  return (
    <article 
      className="activity-card" 
      style={{ borderLeft: `4px solid ${typeStyles.border}` }}
      role={role}
      aria-label={`${activity.type}: ${activity.title}`}
    >
      <div className="activity-header">
        <div 
          className="activity-type-badge" 
          style={{ 
            backgroundColor: typeStyles.bg,
            color: typeStyles.text,
            border: `1px solid ${typeStyles.border}`
          }}
          aria-label={`Post type: ${activity.type}`}
        >
          {activity.type.charAt(0).toUpperCase() + activity.type.slice(1)}
        </div>
        
        {isLocked && (
          <div className="activity-locked-badge" role="status" aria-label="This thread is locked">
            <LockClosedIcon width={14} height={14} aria-hidden="true" />
            <span>Locked</span>
          </div>
        )}
        
        {(canEdit || isAdmin) && (
          <div className="activity-actions">
            {canEdit && (
              <>
                <button
                  onClick={() => onEdit(activity)}
                  className="action-btn edit-btn"
                  title="Edit post"
                  aria-label={`Edit ${activity.type}: ${activity.title}`}
                >
                  <PencilIcon width={16} height={16} aria-hidden="true" />
                  Edit
                </button>
                <LoadingButton
                  onClick={handleDelete}
                  loading={deleting}
                  loadingText="Deleting..."
                  className="action-btn delete-btn"
                  title="Delete post"
                  aria-label={`Delete ${activity.type}: ${activity.title}`}
                >
                  <TrashIcon width={16} height={16} aria-hidden="true" />
                  <span>Delete</span>
                </LoadingButton>
              </>
            )}
            {isAdmin && (
              <LoadingButton
                onClick={handleLock}
                loading={locking}
                loadingText={isLocked ? "Unlocking..." : "Locking..."}
                className={`action-btn ${isLocked ? 'unlock-btn' : 'lock-btn'}`}
                title={isLocked ? "Unlock thread" : "Lock thread"}
                aria-label={isLocked ? `Unlock ${activity.title}` : `Lock ${activity.title}`}
              >
                {isLocked ? (
                  <LockClosedIcon width={16} height={16} aria-hidden="true" />
                ) : (
                  <LockOpenIcon width={16} height={16} aria-hidden="true" />
                )}
                {isLocked ? 'Unlock' : 'Lock'}
              </LoadingButton>
            )}
          </div>
        )}
      </div>

      <div className="activity-content">
        <h3 className="activity-title">{activity.title}</h3>
        
        <div 
          className="activity-body content-preview"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(activity.body) }}
        />

        {activity.type === 'event' && activity.eventDate && (
          <div className="event-details">
            <strong>Event Date:</strong> {formatDate(activity.eventDate)}
          </div>
        )}

        <div className="activity-metadata">
          <div className="activity-meta-left">
            {activity.tags && activity.tags.length > 0 && (
              <div className="activity-tags">
                {activity.tags.map((tag, index) => (
                  <span key={index} className="tag">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
          
          <div className="activity-meta-right">
            <span className="activity-author">{currentUser?.displayName || activity.authorDisplayName}</span>
            <span className="activity-date">{formatDate(activity.createdAt)}</span>
          </div>
        </div>

        {activity.links && activity.links.length > 0 && (
          <div className="activity-links">
            <strong>Links:</strong>
            {activity.links.map((link, index) => (
              <a
                key={index}
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="activity-link"
              >
                {link}
              </a>
            ))}
          </div>
        )}
      </div>

      <div className="activity-footer">
        <div className="activity-reactions">
          {isAuthenticated ? (
            <>
              <button
                onClick={() => onReaction(activity._id, 1)}
                className={`reaction-btn upvote ${userVote === 1 ? 'active' : ''}`}
                disabled={userVote === 1}
                aria-label={`Upvote. Current count: ${activity.reactions?.upvotes || 0}`}
                aria-pressed={userVote === 1}
              >
                <HandThumbUpIcon width={16} height={16} aria-hidden="true" />
                <span>{activity.reactions?.upvotes || 0}</span>
              </button>
              <button
                onClick={() => onReaction(activity._id, -1)}
                className={`reaction-btn downvote ${userVote === -1 ? 'active' : ''}`}
                disabled={userVote === -1}
                aria-label={`Downvote. Current count: ${activity.reactions?.downvotes || 0}`}
                aria-pressed={userVote === -1}
              >
                <HandThumbDownIcon width={16} height={16} aria-hidden="true" />
                <span>{activity.reactions?.downvotes || 0}</span>
              </button>
            </>
          ) : (
            <div className="reaction-display">
              <span className="reaction-count">
                <HandThumbUpIcon width={16} height={16} aria-hidden="true" />
                {activity.reactions?.upvotes || 0}
              </span>
              <span className="reaction-count">
                <HandThumbDownIcon width={16} height={16} aria-hidden="true" />
                {activity.reactions?.downvotes || 0}
              </span>
              <Link to={ROUTES.REGISTER} className="sign-in-to-vote">
                Sign up to vote
              </Link>
            </div>
          )}
        </div>

        <div className="activity-comments-toggle">
          <button
            onClick={() => setShowComments(!showComments)}
            className="comments-toggle-btn"
            aria-label={`${showComments ? 'Hide' : 'Show'} comments. ${activity.comments?.length || 0} comments`}
            aria-expanded={showComments}
          >
            <ChatBubbleLeftIcon width={16} height={16} aria-hidden="true" />
            {activity.comments?.length || 0} {activity.comments?.length === 1 ? 'comment' : 'comments'}
          </button>
        </div>
      </div>

      {showComments && (
        <CommentSection
          activityId={activity._id}
          comments={activity.comments || []}
          currentUser={currentUser}
          isAuthenticated={isAuthenticated}
        />
      )}
    </article>
  );
}

export default ActivityCard;